import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const EditPayoutModal = ({ isOpen, onClose, onUpdate, payout }) => {
  const [formData, setFormData] = useState({
    adjustments: 0,
    deductions: 0,
    description: '',
    status: 'pending'
  });

  useEffect(() => {
    if (payout) {
      setFormData({
        adjustments: payout.adjustments || 0,
        deductions: payout.deductions || 0,
        description: payout.description || '',
        status: payout.status || 'pending'
      });
    }
  }, [payout]);

  if (!isOpen || !payout) return null;

  const finalAmount = (payout.amount + parseFloat(formData.adjustments || 0) - parseFloat(formData.deductions || 0)).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate(payout.id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Payout</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Two Column Layout */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Payout Info & Amounts */}
            <div className="space-y-4">
              {/* Payout Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="mb-2">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Tutor</p>
                  <p className="font-bold text-gray-900 dark:text-white">{payout.tutor_name}</p>
                </div>
                <div className="mb-2">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Period</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Base Amount</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">₹{payout.amount.toLocaleString()}</p>
                </div>
              </div>

              {/* Adjustments */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Adjustments (₹)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.adjustments}
                  onChange={(e) => setFormData({...formData, adjustments: e.target.value})}
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Add bonus or additional amounts
                </p>
              </div>

              {/* Deductions */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Deductions (₹)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.deductions}
                  onChange={(e) => setFormData({...formData, deductions: e.target.value})}
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Deduct taxes, fees, or penalties
                </p>
              </div>
            </div>

            {/* Right Column - Status & Description */}
            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Description */}
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  rows="8"
                  placeholder="Add notes or reason for adjustments"
                />
              </div>
            </div>
          </div>

          {/* Final Amount Preview - Full Width */}
          <div className="bg-green-50 dark:bg-green-900 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 dark:text-green-300">Final Amount</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">₹{finalAmount}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-green-600 dark:text-green-400">
                  Base: ₹{payout.amount} + ₹{formData.adjustments || 0} - ₹{formData.deductions || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Update Payout
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPayoutModal;
